import { type Ref, ref, computed } from 'vue'
import type Swiper from 'swiper'
import { isImage, isSlide } from '@collection-player/utils/slide-type.util'
import type { CollectionSlide } from '@collection-player/types/collection-player.type'

type SwiperElement = HTMLElement & { swiper: Swiper }

const MAX_ZOOM = 2.0
const MIN_ZOOM = 0.4
const ZOOM_THRESHOLD = 0.2
const INITIAL_ZOOM = 1

const roundToOneDecimalPlace = (n: number) => {
  const precision = 10

  return Math.round(n * precision) / precision
}

export default (swiperElement: Ref<SwiperElement | null>, activeSlide: Ref<CollectionSlide | null>) => {
  const zoom = ref(INITIAL_ZOOM)

  const isZoomEnabled = computed(() => activeSlide.value && (isImage(activeSlide.value) || isSlide(activeSlide.value)))

  const resetZoom = () => {
    swiperElement.value?.swiper.zoom.out()
  }

  const zoomIn = () => {
    const currentScale = swiperElement.value?.swiper.zoom.scale ?? INITIAL_ZOOM
    const newScale = Math.min(currentScale + ZOOM_THRESHOLD, MAX_ZOOM)

    swiperElement.value?.swiper.zoom.in(roundToOneDecimalPlace(newScale))
  }

  const zoomOut = () => {
    const currentScale = swiperElement.value?.swiper.zoom.scale ?? INITIAL_ZOOM
    const newScale = Math.max(currentScale - ZOOM_THRESHOLD, MIN_ZOOM)

    swiperElement.value?.swiper.zoom.in(roundToOneDecimalPlace(newScale))
  }

  const onZoomChangeEvent = (event: CustomEvent) => {
    zoom.value = event?.detail?.[1] ?? INITIAL_ZOOM
  }

  return {
    zoom,
    isZoomEnabled,
    resetZoom,
    zoomIn,
    zoomOut,
    onZoomChangeEvent,
  }
}
